/* eslint-disable jsx-a11y/anchor-is-valid */
import React from "react";
import CustomLink from "components/Links/CustomLink";
import dayjs from "dayjs";

import Switch from "@mui/material/Switch";
import ImageBadge from "components/Badge/ImageBadge.js";
import useTranslation from "next-translate/useTranslation";
import Specification from "components/Specifications/Specification";
import useDeals from "modules/products/useDeals";
import LinkBadge from "components/LinkBadge/LinkBadge";
import PriceBand from "components/PriceBand/PriceBand.js";
import Card from "components/Card/Card";

import MoreButton from "components/CustomButtons/MoreButton";

import Tooltip from "@mui/material/Tooltip";
import TooltipAndIcon from "components/Tooltip/TooltipAndIcon";

import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SkeletonWithFade from "components/Loading/SkeletonWithFade";

import ProductAthletesAvatarGroup from "components/ProductAthletes/ProductAthletesAvatarGroup";

import { PriceComparisonModule } from "components/PriceComparison/PriceComparisonModule";
import {
  maxComparisonProducts,
  MONTH_YEAR_FORMAT,
} from "settings/global-website-config";

import { makeStyles } from "tss-react/mui";

import ExpertReviewScoreBadge from "components/Badge/ExpertReviewScoreBadge";

import { FeaturedImage } from "components/Image/FeaturedImage";

import Table from "components/Table/Table";

import { grayColor } from "../../assets/jss/nextjs-material-kit-pro";

const useStyles = makeStyles()((theme) => ({
  addTopAndBottomMargin: {
    marginTop: "4px",
    marginBottom: "4px",
  },

  columnTitle: {
    textAlign: "center",
    fontSize: "15px",
    marginBottom: "10px",
  },
  nowViewingBorder: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.secondary.main}`,
    "& img": {
      padding: "5px !important",
    },
    //    padding: '5px', // Removed because it knocks titles out of line. Target image (above) only if need
  },
  verticalMiddleContainer: {
    height: "100%",
    display: "flex", // Not perfect, leaves first column with title out of line
    alignItems: "center",
    justifyContent: "center",
  },

  nowViewingBadge: { margin: "-10px 0px 0px -3px !important" },

  fixedImageHeaderHeight: { height: "142px" }, // Was 242 but left massive blank space //Prevents layyout shift when selecting different related products types
  tableHeader: {
    padding: theme.spacing(1),
    //  maxWidth: '200px',
    //  width: '150px',
    //minWidth: '150px',

    // verticalAlign: 'top', <- was messing with Deals row, not sure it was impacting anything else?
    textAlign: "center",
    "&svg": { marginBottom: theme.spacing(0.75) },
    "& img": {
      width: "134px", //150 -16px(padding) <--- May be required to prevent images shrinkng, but causes slight changes in size depending on whether there is a deal in the deals row
      height: "auto",
      maxWidth: "134px",
      maxHeight: "200px",
      //height: '150px',
      marginBottom: theme.spacing(1),
      borderRadius: theme.spacing(0.75),
    },
  },
  tableColumn: {
    textAlign: "center",
    position: "relative",
    // verticalAlign: 'middle', // Might make the buttons inconsistent height, but "top" makes the "product unavailable" entry in the wrong place, so may need to do something dynamic ie different for whether there is a deal or not
  },
  highlightColumn: {
    fontWeight: "400",
    color: theme.palette.primary.main,
    backgroundColor: grayColor[2],
    //  verticalAlign: 'middle', Removed as it interferes with deal blocks. Add elsewhere if needed back
    position: "sticky",
    zIndex: 15,
    left: 0,
    backgroundClip: "padding-box",
    borderTop: "1px solid white !important",
    //...boxShadow,
  },
  highlightHeaderColumn: { position: "sticky", zIndex: "101", left: "0" },
  disableClick: {
    pointerEvents: "none",
  },
  comparisonContainer: {
    //    position: 'relative', // Allows fadeOverlay to be correctly sized but causes buttons to be mislocated
    height: "100%",
    [theme.breakpoints.down("md")]: {
      // marginLeft: '-10px',
      //  marginRight: '-10px',
      // overflow: 'hidden',
    },
  },
  similarItem: {
    fontSize: "12px",
    lineHeight: "1rem",
    minHeight: "2.1rem",
    display: "inline-grid",
    "& svg": {
      fontSize: "14px",
      marginRight: theme.spacing(0.5),
    },
    // margin: '0px',
    // padding: '0px',
  },
  inlineGrid: {
    // height: '100%',
    //  verticalAlign: 'middle',
    display: "inline-grid",
    verticalAlign: "bottom",
  },
  verticallyCenteredTitle: { position: "unset", display: "inline-block" },
  centerVertically: {
    display: "inline-flex",
    alignItems: "center",
    verticalAlign: "middle",
  },
}));

const useLabelStyles = makeStyles()((theme) => ({
  rowTitle: {
    //  position: 'absolute',
    // top: '0px',
    //  right: '0px',
    // left: '0px',
    height: "100%",
    color: theme.palette.primary.main,
  },
  negTopMargin: {
    marginTop: "-18px", // Place it in the padding of surrounding block
  },
  tableLink: {
    fontWeight: 400,
    //  color: darkCustomColor,
  },
}));
const TooltipLinkBadge = (props) => {
  const { tooltipTitle, name, href } = props;

  //  Spread the props to the underlying DOM element.
  return (
    <Tooltip component="a" id={tooltipTitle} title={tooltipTitle}>
      <span>
        <LinkBadge name={name} href={href} disabled={!href} />
      </span>
    </Tooltip>
  );
};

const LabelComponent = (labelProps) => {
  const { customChildClass, labelContent, hasContent } = labelProps;

  const { classes, cx } = useLabelStyles();
  const { t } = useTranslation();

  const { name, uri: linkUri, translate, content } = labelContent;

  const tooltipContent =
    (translate && content ? t(`product:descriptions.${name}`) : content) || "";

  return (
    <h6
      className={cx({
        [classes.rowTitle]: true,
        [classes.negTopMargin]: hasContent,
        [customChildClass]: !!customChildClass,
      })}
    >
      {translate ? (
        t(`product:specs.${name}`)
      ) : linkUri ? (
        <CustomLink href={linkUri} className={classes.tableLink}>
          {name}
        </CustomLink>
      ) : (
        name
      )}
      <TooltipAndIcon title={tooltipContent} />
    </h6>
  );
};

const LineLabel = (props) => {
  const { index, lineIndex, line, ...rest } = props;
  return (
    index === 1 && (
      <LabelComponent
        {...rest}
        key={`label-${lineIndex}`}
        labelContent={line[0]}
      />
    )
  );
};

const ComparisonBlock = (props) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const {
    relatedProducts,
    state,
    product,
    showMoreButton,
    moreButtonText,
    loading,
    sizeLocaleOptions,
    columnsLoading,
    canUpdateFromServer,
    uri,
    mergeDealsState,
    id,
    containerWidth,
    sizeSelectRef,
    supportedChoiceTypes,
  } = props;

  const [showDeals, setShowDeals] = React.useState(false);

  const dealsLoaded = [];

  let { dealsLocale } = props;
  dealsLocale = dealsLocale.toLowerCase();

  const customHeadCellClasses = [];
  const customHeadClassesForCells = [];

  const {
    headersArray,
    tableArray: rawTableArray,
    uri: relatedProductsUri,
    type,
    includeCurrentProduct,
    products,
    isChildColumn,
    parentChildColumnVersion,
  } = relatedProducts;

  const showLimit =
    relatedProducts &&
    products &&
    products.nodes.length <= maxComparisonProducts
      ? products.nodes.length - 1
      : maxComparisonProducts;

  const tableHead = headersArray.map((header, index) => {
    if (!header) return "";

    if (index !== 0) {
      const headerClasses = cx({
        [classes.tableHeader]: true,
        [classes.fixedImageHeaderHeight]: true,
        [classes.highlightHeaderColumn]: index === 1 && includeCurrentProduct,
      });
      customHeadCellClasses.push(headerClasses);
      customHeadClassesForCells.push(index);
    }
    const isCurrentProduct = index === 1 && includeCurrentProduct;

    const cardClasses = cx({
      [classes.nowViewingBorder]: isCurrentProduct,
    });

    const columnTitleClasses = cx({
      [classes.columnTitle]: true,
      [classes.disableClick]: !header.uri,
    });
    const showLoadingSkeleton =
      loading || ((index !== 1 || !includeCurrentProduct) && columnsLoading);

    return (
      <React.Fragment key={index}>
        <Card plain noMargin className={cardClasses}>
          <FeaturedImage
            featuredImage={
              header.featuredImage?.node ||
              header.featuredImage ||
              product.category.node.categoryDetails.fallbackImage
            }
            fallbackAltText={header.title}
            href={index === 0 ? null : header.uri}
            loading={showLoadingSkeleton}
            widthOverride={150}
            heightOverride={100}
            maxHeight={120}
            displayFlexWhenLoading
            badge={
              isCurrentProduct && (
                <ImageBadge
                  label={t("product:nowViewing")}
                  customClasses={classes.nowViewingBadge}
                />
              )
            }
            type={type}
          />

          {header.title && (
            <>
              {" "}
              <CustomLink
                href={header.uri || "#"}
                className={columnTitleClasses}
              >
                <SkeletonWithFade
                  inProp={showLoadingSkeleton}
                  width="60%"
                  marginBottom="8px"
                >
                  {header.title}
                </SkeletonWithFade>
              </CustomLink>
            </>
          )}
        </Card>
      </React.Fragment>
    );
  });

  const customCellClasses = [];
  const customClassesForCells = [];
  const sharedTermsClassesForCells = [];
  const customThinLineClassesForCells = [];

  const tableClasses = cx(classes.tableColumn);

  /// TODO : ADD A SLICE HERE TO REDUCE BASED ON OFFSET

  for (const [index, _product] of products.nodes.entries()) {
    // const tableClasses = classNames({ [classes.tableColumn]: true, });
    const force = false;
    if (index === 0) {
      if (includeCurrentProduct || force) {
        customCellClasses.push(`${tableClasses} ${classes.highlightColumn}`);
      } else {
        customCellClasses.push(tableClasses);
      }
      customClassesForCells.push(index + 1);
    }

    customCellClasses.push(tableClasses);
    customClassesForCells.push(index + 2);
  }

  const dealsState = useDeals(
    relatedProducts?.products.nodes,
    state,
    dealsLocale,
    {
      isVisible: showDeals,
      liteOnly: true,
      noSizeOrSex: supportedChoiceTypes?.includes("none"),
    },
    mergeDealsState
  );

  const createTableArray = (array) =>
    array.map((line, lineIndex) => {
      /// TODO : ADD A SLICE HERE TO REDUCE BASED ON OFFSET & ColumnsToShow
      const lineName = line[0].name;

      if (lineName !== "sharedTerms") {
        if (lineName === "similarity") {
          customThinLineClassesForCells.push(lineIndex);
        }

        return line.map((l, index) => {
          if (index === 0) {
            return l.name; // for tracking later
          } else {
            const hasContentLabelNames = ["priceBand", "hasDeal"];

            const hasContent = !!l || hasContentLabelNames.includes(lineName);

            const lineLabelProps = {
              index,
              lineIndex,
              line,
              hasContent,
            };

            const showLoadingSkeleton =
              loading ||
              ((index !== 1 || !includeCurrentProduct) && columnsLoading);

            if (lineName === "similarity") {
              return (
                <>
                  <LineLabel
                    {...lineLabelProps}
                    customContainerClass={classes.inlineGrid}
                    customChildClass={classes.verticallyCenteredTitle}
                  />

                  {l && (
                    <div
                      className={`${classes.similarItem} ${classes.tableColumn}`}
                    >
                      <div className={classes.centerVertically}>
                        {" "}
                        <CheckCircleOutlineIcon />
                        {t("product:verySimilar")}
                      </div>
                    </div>
                  )}
                </>
              );
            }

            if (lineName === "expertReviewScore") {
              return (
                <>
                  <LineLabel {...lineLabelProps} />
                  <div className={classes.addTopAndBottomMargin}>
                    <SkeletonWithFade inProp={columnsLoading} count={2}>
                      <ExpertReviewScoreBadge
                        reviews={l}
                        showScore
                        hideHeader
                        hideDetails
                        href={l.uri || "#"}
                        textCenter
                        hideArrow
                        loading={showLoadingSkeleton}
                        showLoadingSkeleton
                        type={type}
                      />
                    </SkeletonWithFade>
                  </div>
                </>
              );
            }
            if (lineName === "productAthletes") {
              return (
                <>
                  <LineLabel {...lineLabelProps} />
                  <ProductAthletesAvatarGroup
                    productAthletes={l.productAthletes}
                    notClickable
                    center
                    max={3}
                    loading={showLoadingSkeleton}
                  />
                </>
              );
            }
            if (lineName === "priceBand") {
              return (
                <>
                  <LineLabel {...lineLabelProps} />
                  <PriceBand priceBand={l} dealsLocale={dealsLocale} />
                </>
              );
            }
            if (lineName === "released") {
              return (
                <>
                  <LineLabel {...lineLabelProps} />
                  {l && dayjs(l).format(MONTH_YEAR_FORMAT)}
                </>
              );
            }
            if (lineName === "bestDeal") {
              /*   const dealsState = useDeals(l, state, dealsLocale, {
                isVisible: showDeals,
                liteOnly: true,
                // preventFetch: !showDeals,
              }); */
              const hide = parseInt(process.env.ALWAYS_SHOW_SHOW_DEALS_SWITCH);
              if (hide || !showDeals) {
                if (index === 1) {
                  return (
                    <>
                      {t("product:showDeals")}
                      <Switch
                        checked={showDeals}
                        onChange={() => setShowDeals(!showDeals)}
                        inputProps={{ "aria-label": "show-deals" }}
                      />
                    </>
                  );
                }
                return;
              }

              return (
                index !== 0 && (
                  <div
                    className={cx({
                      [classes.verticalMiddleContainer]:
                        index !== 1 &&
                        !dealsState.deals[l.databaseId]?.allDeals,
                    })}
                  >
                    <LineLabel {...lineLabelProps} />
                    <PriceComparisonModule
                      sizeLocaleOptions={sizeLocaleOptions}
                      key={`${l.id}-deals`}
                      dealsState={dealsState}
                      product={l}
                      state={state}
                      type={type}
                      onlyDeal
                      loading={showLoadingSkeleton}
                      isVisible
                      compact
                      comparisonBlock
                      sizeSelectRef={sizeSelectRef}
                      supportedChoiceTypes={supportedChoiceTypes}
                    />
                  </div>
                )
              );
            }

            if (typeof line[0] === "object") {
              return (
                <>
                  <LineLabel {...lineLabelProps} />
                  <Specification
                    customClasses={classes.addTopAndBottomMargin}
                    spec={l}
                    loading={showLoadingSkeleton}
                  />
                </>
              );
            }
          }
        });
      }
      // Got this far, it must be shared terms

      sharedTermsClassesForCells.push(lineIndex);

      return [
        "sharedTerms",
        <>
          {line.map((s, i) =>
            i === 0 ? (
              <LabelComponent
                key={`label-${lineIndex}-${i}`}
                labelContent={s}
                hasContent
              />
            ) : (
              <TooltipLinkBadge
                key={`label-${lineIndex}-${i}`}
                name={s.term.name}
                href={s.term.href}
                disabled={!s.term.href}
                tooltipTitle={
                  s.taxonomy.translate
                    ? t(`product:taxonomies.${s.taxonomy.slug}`)
                    : s.taxonomy.name
                }
              />
            )
          )}
        </>,
      ];
    });

  const tableArray = createTableArray(rawTableArray);

  const footerArray =
    tableArray[tableArray.length - 1][0] === "sharedTerms"
      ? tableArray.pop()
      : null; // In the current implementation the footer is always the last item in the array. Should be more robust though really.

  return (
    <div className={classes.comparisonContainer}>
      <Table
        containerWidth={containerWidth}
        hideFirstColumn
        striped
        hover
        showDeals={showDeals}
        showLimit={showLimit}
        state={state}
        dealsLoaded={dealsLoaded}
        canUpdateFromServer={canUpdateFromServer}
        tableHead={tableHead}
        tableArray={tableArray}
        footerArray={footerArray}
        scrollable
        customCellClasses={customCellClasses}
        customClassesForCells={customClassesForCells}
        customHeadCellClasses={customHeadCellClasses}
        sharedTermsClassesForCells={sharedTermsClassesForCells}
        customThinLineClassesForCells={customThinLineClassesForCells}
        customHeadClassesForCells={customHeadClassesForCells}
        relatedProducts={relatedProducts}
        uri={uri}
        id={id}
        loading={columnsLoading}
        isChildColumn={isChildColumn}
        parentChildColumnVersion={parentChildColumnVersion}
        tableName="comparisonBlock"
      />
      <MoreButton
        show={showMoreButton && relatedProductsUri}
        uri={relatedProductsUri}
        text={moreButtonText}
        loading={loading}
        noPrefetch
        noFollow
      />
    </div>
  );
};

export default ComparisonBlock;
